import { useState } from 'react';
import { Badge, Button, Csel, Icon, Input, Reason, Toggle, useToast } from '@efira/ui';

import { useSaveStream, useStream } from '../../api/queries';

/**
 * Настройки эфира: голос, чтец, оповещения, фильтр слов, шаблоны, длительность.
 *
 * ГОЛОСА ПРИХОДЯТ ОТ ОВЕРЛЕЯ. Список зависит от машины и системы, где открыт
 * браузерный источник, а не от нашего сервера: показано ровно столько
 * голосов, сколько их есть. Восемнадцать голосов эталона мы не обещаем.
 *
 * ЧТЕЦ КОММЕНТАРИЕВ И ЧТЕНИЕ СООБЩЕНИЙ - РАЗНЫЕ ТУМБЛЕРЫ, и в интерфейсе
 * видно, что разные: первый читает зрителей, второй - ваши сообщения в кадр.
 */
export function StreamSection() {
  const stream = useStream();
  const save = useSaveStream();
  const toast = useToast();

  const data = stream.data;
  const voices = data?.voices ?? [];

  /* Голоса разложены по языкам: русский и английский в начале - это те два,
     на которых идёт эфир. */
  const byLang = new Map<string, { name: string; lang: string }[]>();

  for (const voice of voices) {
    const lang = voice.lang.slice(0, 2).toLowerCase();
    byLang.set(lang, [...(byLang.get(lang) ?? []), voice]);
  }

  const langs = [...byLang.keys()].sort((a, b) => rank(a) - rank(b));

  return (
    <div data-stream="root" className="flex flex-col gap-4">
      <h2 className="text-foreground text-xl font-semibold">Эфир</h2>

      {/* Вводная строка раздела: что в нём и, главное, когда применяется. */}
      <p className="text-foreground/90 text-sm">
        Чтец комментариев, оповещения, сообщения зрителям и пропуск слов. Изменения применяются к
        идущему эфиру сразу — перезапускать ничего не нужно.
      </p>

      <section className="flex flex-col gap-2">
        <h3 className="text-foreground/90 text-sm font-semibold tracking-wide uppercase">
          Голос озвучки
        </h3>

        {/* Активный голос - строкой с индикатором и языком: состояние
            читается цветом, а не только словом «Сейчас». */}
        <p
          data-stream="voice-current"
          className="border-foreground/10 rounded-lg flex items-center gap-2 border p-2 text-sm"
        >
          <span className="bg-primary size-2 shrink-0 rounded-full" aria-hidden="true" />
          <span className="text-foreground min-w-0 flex-1 truncate font-medium">
            {data?.voice ?? 'Голос по умолчанию'}
          </span>
          <span className="text-foreground/80 shrink-0 text-xs">
            {LANG_NAMES[
              (voices.find((voice) => voice.name === data?.voice)?.lang ?? 'ru')
                .slice(0, 2)
                .toLowerCase()
            ] ?? 'язык системы'}
          </span>
        </p>

        {voices.length === 0 ? (
          <p data-stream="voice-empty" className="text-foreground/80 text-sm">
            Откройте оверлей — список голосов появится здесь. Голоса берёт браузерный источник, то
            окно, которое вставлено в OBS.
          </p>
        ) : (
          <ul data-stream="voice-list" className="flex max-h-64 flex-col gap-1 overflow-y-auto">
            {langs.map((lang) => (
              <li key={lang}>
                <p className="text-foreground/80 px-1 py-1 text-xs tracking-wide uppercase">
                  {LANG_NAMES[lang] ?? lang}
                </p>
                <ul className="flex flex-col gap-1">
                  {(byLang.get(lang) ?? []).map((voice) => (
                    <li
                      key={voice.name}
                      data-stream-voice={voice.name}
                      data-control-row="voice-row"
                      className="border-foreground/10 rounded-lg flex items-center gap-2 border p-2"
                    >
                      <span className="text-foreground min-w-0 flex-1 truncate text-sm">
                        {voice.name}
                      </span>

                      <Button
                        variant="ghost"
                        size="s"
                        className="px-1"
                        data-stream-voice-try={voice.name}
                        aria-label={`Прослушать «${voice.name}»`}
                        title="Прослушать здесь — в эфир не уйдёт"
                        onClick={() => {
                          speakHere('Проверка голоса', voice.name);
                        }}
                      >
                        <Icon name="play" size="sm" />
                      </Button>

                      {/* У активного - МЕТКА, а не кнопка: нажимать на то,
                          что уже выбрано, незачем. */}
                      {data?.voice === voice.name ? (
                        <Badge
                          data-stream-voice-active={voice.name}
                          variant="default"
                          className="shrink-0"
                        >
                          выбран
                        </Badge>
                      ) : (
                        <Button
                          variant="secondary"
                          size="s"
                          className="shrink-0"
                          data-stream-voice-pick={voice.name}
                          onClick={() => {
                            save.mutate({ voice: voice.name });
                          }}
                        >
                          Выбрать
                        </Button>
                      )}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        )}
      </section>

      <section className="flex flex-col gap-2">
        <h3 className="text-foreground/90 text-sm font-semibold tracking-wide uppercase">
          Что показывать на оверлее
        </h3>

        <StreamSwitch
          id="reader"
          title="Чтец комментариев"
          hint="Читает вслух то, что пишут зрители. Голос — из списка выше."
          checked={data?.reader === true}
          onChange={(next) => {
            save.mutate({ reader: next });
          }}
        />

        <StreamSwitch
          id="read-shoutouts"
          title="Читать вслух ваши сообщения"
          hint="Ваши сообщения зрителям озвучиваются отдельно от комментариев."
          checked={data?.readShoutouts === true}
          onChange={(next) => {
            save.mutate({ readShoutouts: next });
          }}
        />

        <StreamSwitch
          id="alerts"
          title="Оповещения"
          hint="Карточка на оверлее: подарок, подписка, репост, вход зрителя и вехи."
          checked={data?.alerts === true}
          onChange={(next) => {
            save.mutate({ alerts: next });
          }}
        />

        <label data-control-row="stream-duration" className="flex flex-col gap-1 text-sm">
          Длительность показа
          <Csel
            value={String(data?.seconds ?? 10)}
            aria-label="Длительность показа"
            triggerClassName="w-44"
            options={Array.from({ length: 12 }, (_, index) => {
              const value = String((index + 1) * 5);
              return { value, label: `${value} с` };
            })}
            onChange={(next) => {
              save.mutate({ seconds: Number(next) });
            }}
          />
        </label>
      </section>

      <MuteWords />

      <Shoutouts
        onSaved={() => {
          toast.show({ tone: 'success', title: 'Шаблоны сохранены' });
        }}
      />
    </div>
  );
}

const LANG_NAMES: Record<string, string> = {
  ru: 'Русский',
  en: 'Английский',
};

function rank(lang: string): number {
  return lang === 'ru' ? 0 : lang === 'en' ? 1 : 2;
}

/** Прослушивание голоса ЗДЕСЬ: в эфир не уходит, и так подписано. */
function speakHere(text: string, voiceName: string): void {
  const speech = window.speechSynthesis;

  if (speech === undefined) {
    return;
  }

  const utterance = new SpeechSynthesisUtterance(text);
  const voice = speech.getVoices().find((item) => item.name === voiceName);

  if (voice !== undefined) {
    utterance.voice = voice;
  }

  speech.speak(utterance);
}

function StreamSwitch({
  id,
  title,
  hint,
  checked,
  onChange,
}: {
  id: string;
  title: string;
  hint: string;
  checked: boolean;
  onChange: (next: boolean) => void;
}) {
  return (
    /* Роль «переключатель настройки» - компонент `Toggle` продукта, один на
       весь продукт (таблица ролей, `docs/DESIGN_SYSTEM.md` 0.4). Прежде
       здесь была своя вёрстка с тремя уровнями текста, набранными по
       месту, - и из-за неё переключатели настроек эфира выглядели иначе,
       чем такие же в левой колонке. */
    <div data-stream-switch={id}>
      <Toggle checked={checked} onChange={onChange} label={title} hint={hint} />
    </div>
  );
}

/**
 * Пропуск помеченных слов.
 *
 * Применяется ДО СИНТЕЗА И ДО ПОКАЗА, на сервере. «После» тут не бывает:
 * слово, уже прозвучавшее в эфире, обратно не забрать.
 */
function MuteWords() {
  const stream = useStream();
  const save = useSaveStream();
  const [word, setWord] = useState('');

  const words = stream.data?.customWords ?? [];

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-foreground/90 text-sm font-semibold tracking-wide uppercase">
        Пропуск помеченных слов
      </h3>

      <StreamSwitch
        id="profanity"
        title="Общий фильтр брани"
        hint="Наш список мата и оскорблений на русском и английском. Слова заменяются звёздочками до того, как прозвучат."
        checked={stream.data?.profanity === true}
        onChange={(next) => {
          save.mutate({ profanity: next });
        }}
      />

      <div data-control-row="mute-words" className="@container flex items-center gap-2">
        <Input
          data-stream="custom-word"
          aria-label="Своё слово"
          placeholder="Добавить своё слово"
          value={word}
          onChange={(event) => {
            setWord(event.target.value);
          }}
          className="min-w-0 flex-1"
        />
        <Button
          variant="primary"
          size="s"
          data-stream="custom-add"
          disabled={word.trim() === ''}
          aria-describedby={word.trim() === '' ? 'stream-word-why' : undefined}
          onClick={() => {
            save.mutate({ customWords: [...words, word.trim()] });
            setWord('');
          }}
        >
          Добавить
        </Button>
      </div>
      {word.trim() === '' && (
        <Reason id="stream-word-why">Впишите слово — кнопка станет доступна.</Reason>
      )}

      {words.length === 0 ? (
        <p data-stream="custom-empty" className="text-foreground/90 text-sm">
          Своих слов нет. Добавьте те, которые не должны звучать в вашем эфире, — имена, спойлеры,
          что угодно.
        </p>
      ) : (
        <ul data-stream="custom-words" className="flex flex-wrap gap-1">
          {words.map((item) => (
            <li key={item}>
              <Button
                variant="danger"
                className="gap-1"
                data-stream-word={item}
                data-removal="stream-word-remove"
                aria-label={`Убрать «${item}»`}
                onClick={() => {
                  save.mutate({ customWords: words.filter((other) => other !== item) });
                }}
              >
                {item}
                <Icon name="dismiss" size="sm" />
              </Button>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Шаблоны сообщения зрителю: подстановки видны и проверяются. */
function Shoutouts({ onSaved }: { onSaved: () => void }) {
  const stream = useStream();
  const save = useSaveStream();

  const tokens = stream.data?.tokens ?? ['{имя}', '{логин}'];
  const shoutout = stream.data?.shoutout ?? { user: '', plain: '' };

  const [user, setUser] = useState<string | null>(null);
  const [plain, setPlain] = useState<string | null>(null);

  const userValue = user ?? shoutout.user;
  const plainValue = plain ?? shoutout.plain;

  /* Подстановка, которой нет в списке, - это опечатка, которая в эфире
     покажется зрителю как есть. Поэтому она названа до сохранения. */
  const unknown = [...userValue.matchAll(/\{[^}]+\}/g)]
    .map((match) => match[0])
    .filter((token) => !tokens.includes(token));

  return (
    <section className="flex flex-col gap-2">
      <h3 className="text-foreground text-sm font-semibold">Сообщение зрителю</h3>

      <p className="text-foreground/80 text-sm">
        Подстановки: {tokens.join(', ')}. Пустой шаблон разрешён — тогда сообщение уйдёт без текста,
        и на оверлее будет только имя.
      </p>

      <label data-control-row="shoutout-user" className="flex flex-col gap-1 text-sm">
        Для конкретного зрителя
        <Input
          data-stream="shoutout-user"
          value={userValue}
          onChange={(event) => {
            setUser(event.target.value);
          }}
        />
      </label>

      {unknown.length > 0 && (
        <p data-stream="shoutout-unknown" className="text-warning text-sm">
          Такой подстановки нет: {unknown.join(', ')}. На оверлее она покажется как есть.
        </p>
      )}

      <label className="flex flex-col gap-1 text-sm">
        Без обращения
        <Input
          data-stream="shoutout-plain"
          value={plainValue}
          onChange={(event) => {
            setPlain(event.target.value);
          }}
        />
      </label>

      <Button
        variant="primary"
        size="s"
        className="self-start"
        data-stream="shoutout-save"
        onClick={() => {
          save.mutate({ shoutout: { user: userValue, plain: plainValue } }, { onSuccess: onSaved });
        }}
      >
        Сохранить шаблоны
      </Button>
    </section>
  );
}
